import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { config } from '@config/pendo.config.json';
import { submitMentoringProgramReflectionResponses } from 'redux/actions/mentoring-program-reflections';
import { selectMentoringProgramReflectionIsSubmitting } from 'redux/selectors/mentoring-program-reflections';
import {
  largeSpacing,
  quarterSpacing,
  standardSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import { fadeInAnimation } from './styles';
import AthenaTextarea from 'athena/components/textarea';
import { Button } from 'react-bootstrap';
import { MentoringProgramReflectionQuestion } from 'redux/schemas/models/mentoring-program-reflections';

const cardStyles = css`
  ${fadeInAnimation};
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: ${standardSpacing}px;
  padding: ${largeSpacing}px;
  gap: 16px;

  .reflection-content-container {
    gap: ${threeQuartersSpacing}px;
  }

  .reflection-questions-list {
    gap: ${standardSpacing}px;
  }

  .reflection-question {
    gap: ${quarterSpacing}px;
  }
`;

type ReflectionFormProps = {
  programId: number;
  connectionId: number;
  questions: MentoringProgramReflectionQuestion[];
  connectionFirstName: string;
};

const ReflectionForm = ({
  programId,
  connectionId,
  questions,
  connectionFirstName,
}: ReflectionFormProps) => {
  const dispatch = useAppDispatch();
  const isSubmitting = useSelector((state) =>
    selectMentoringProgramReflectionIsSubmitting(state, connectionId)
  );

  const sortedQuestions = useMemo(
    () => [...questions].sort((a, b) => a.questionIndex - b.questionIndex),
    [questions],
  );

  const [answers, setAnswers] = useState<Record<number, string>>({});

  const translationBase =
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REFLECTION_WORKSPACE.SHARED_REFLECTIONS;
  const dataQATag =
    config.pendo.athena.mentorshipProgram.participantHome.programReflections
      .modal.sharedReflections;

  const allAnswered = questions.every((q) => answers[q.id]?.trim());

  const handleSubmit = () => {
    const responsePayload = questions.map((q) => ({
      questionId: q.id,
      response: answers[q.id] ?? '',
    }));
    dispatch(
      submitMentoringProgramReflectionResponses({
        programId,
        connectionId,
        responses: responsePayload,
      })
    );
  };

  return (
    <div css={cardStyles} className='d-flex flex-column text-left'>
      <p className='heading-3 black mb-0'>{translationBase.CARD_TITLE()}</p>
      <div className='reflection-content-container d-flex flex-column align-items-center'>
        <p className='text-regular gray-1 mb-0 w-100'>
          {translationBase.CARD_SUBTITLE()}
        </p>
        <div className='reflection-questions-list d-flex flex-column w-100'>
          {sortedQuestions.map(({ id, questionText }) => (
            <div key={id} className='reflection-question d-flex flex-column'>
              <p className='text-large-body semi-bold mb-0'>{questionText}</p>
              <AthenaTextarea
                showLabel={false}
                value={answers[id] ?? ''}
                onChange={(e) =>
                  setAnswers((prev) => ({ ...prev, [id]: e.target.value }))
                }
              />
            </div>
          ))}
        </div>
        <Button
          type='button'
          className='btn btn-primary'
          disabled={!allAnswered || isSubmitting}
          onClick={handleSubmit}
          data-qa={dataQATag.submit}
        >
          {t.FORM.SUBMIT()}
        </Button>
      </div>
      <p className='text-small gray-1 font-italic text-center mb-0'>
        {translationBase.FOOTER_NOTE(connectionFirstName)}
      </p>
    </div>
  );
};

export default ReflectionForm;
